import {
    AccrualImporterColumn,
    AccrualImporterGroupBy,
    AccrualImporterView,
} from "@common/types/views/accrualImporterViews";
import { NamedView, ViewType } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { AccrualImporterDraftSort } from "./accrualImporterDraftsSort";
import { AccrualImporterViewRow } from "./accrualImporter.types";
import {
    AccrualImporterActionsCell,
    AccrualDraftCounterpartyCell,
    AccrualImporterDocumentNumberCell,
    AccrualImporterEntityCell,
    AccrualImporterStatusCell,
    AccrualImporterTotalCell,
} from "./accrualDocumentImporterCells";
import {
    AccrualImporterDateImportedCell,
    AccrualImporterDueDateCell,
    AccrualImporterInvoiceDateCell,
} from "./accrualImporterPageCells";
import {
    AccrualImporterAccrualAccountCell,
    AccrualImporterBalanceAccountCell,
    AccrualImporterDescriptionCell,
    AccrualImporterItemCell,
} from "./accrualImporterLineItemCells";
import { withExtractingSkeleton } from "./withExtractingSkeleton";

const InvoiceDateCell = withExtractingSkeleton(AccrualImporterInvoiceDateCell);
const DocumentNumberCell = withExtractingSkeleton(
    AccrualImporterDocumentNumberCell,
);
const CounterpartyCell = withExtractingSkeleton(AccrualDraftCounterpartyCell);
const ItemCell = withExtractingSkeleton(AccrualImporterItemCell);
const DescriptionCell = withExtractingSkeleton(AccrualImporterDescriptionCell);
const AccrualAccountCell = withExtractingSkeleton(
    AccrualImporterAccrualAccountCell,
);
const BalanceAccountCell = withExtractingSkeleton(
    AccrualImporterBalanceAccountCell,
);
const DueDateCell = withExtractingSkeleton(AccrualImporterDueDateCell);
const DateImportedCell = withExtractingSkeleton(
    AccrualImporterDateImportedCell,
);
const TotalCell = withExtractingSkeleton(AccrualImporterTotalCell);
const EntityCell = withExtractingSkeleton(AccrualImporterEntityCell);

interface PageColumnLabels {
    date: string;
    number: string;
    counterparty: string;
    accrualAccount: string;
    balanceAccount: string;
}

export function getAccrualImporterPageViewConfig(
    labels: PageColumnLabels,
): ViewConfig<
    AccrualImporterViewRow,
    AccrualImporterColumn,
    AccrualImporterDraftSort
> {
    return {
        columns: {
            [AccrualImporterColumn.DOCUMENT_DATE]: {
                accessorKey: "id",
                name: labels.date,
                header: labels.date,
                cell: InvoiceDateCell,
                sort: {
                    asc: AccrualImporterDraftSort.DOCUMENT_DATE_ASC,
                    desc: AccrualImporterDraftSort.DOCUMENT_DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [AccrualImporterColumn.NUMBER]: {
                accessorKey: "id",
                name: labels.number,
                header: labels.number,
                cell: DocumentNumberCell,
                sort: {
                    asc: AccrualImporterDraftSort.DOCUMENT_NUMBER_ASC,
                    desc: AccrualImporterDraftSort.DOCUMENT_NUMBER_DESC,
                    defaultOrder: "asc",
                },
                minSize: 120,
                maxSize: 160,
            },
            [AccrualImporterColumn.COUNTERPARTY]: {
                accessorKey: "id",
                name: labels.counterparty,
                header: labels.counterparty,
                cell: CounterpartyCell,
                sort: {
                    asc: AccrualImporterDraftSort.COUNTERPARTY_ASC,
                    desc: AccrualImporterDraftSort.COUNTERPARTY_DESC,
                    defaultOrder: "asc",
                },
                minSize: 160,
                maxSize: 260,
            },
            [AccrualImporterColumn.ITEM]: {
                accessorKey: "id",
                name: "Item",
                header: "Item",
                cell: ItemCell,
                minSize: 180,
                maxSize: 240,
            },
            [AccrualImporterColumn.DESCRIPTION]: {
                accessorKey: "id",
                name: "Description",
                header: "Description",
                cell: DescriptionCell,
                minSize: 220,
                maxSize: 300,
            },
            [AccrualImporterColumn.ACCRUAL_ACCOUNT]: {
                accessorKey: "id",
                name: labels.accrualAccount,
                header: labels.accrualAccount,
                cell: AccrualAccountCell,
                minSize: 140,
                maxSize: 260,
            },
            [AccrualImporterColumn.BALANCE_ACCOUNT]: {
                accessorKey: "id",
                name: labels.balanceAccount,
                header: labels.balanceAccount,
                cell: BalanceAccountCell,
                minSize: 140,
                maxSize: 260,
            },
            [AccrualImporterColumn.STATUS]: {
                accessorKey: "id",
                name: "Status",
                header: "Status",
                cell: AccrualImporterStatusCell,
                minSize: 160,
                maxSize: 240,
                sort: {
                    asc: AccrualImporterDraftSort.IMPORT_STATUS_ASC,
                    desc: AccrualImporterDraftSort.IMPORT_STATUS_DESC,
                    defaultOrder: "asc",
                },
            },
            [AccrualImporterColumn.DUE_DATE]: {
                accessorKey: "id",
                name: "Due date",
                header: "Due date",
                cell: DueDateCell,
                sort: {
                    asc: AccrualImporterDraftSort.DUE_DATE_ASC,
                    desc: AccrualImporterDraftSort.DUE_DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [AccrualImporterColumn.DATE_IMPORTED]: {
                accessorKey: "id",
                name: "Created on",
                header: "Created on",
                cell: DateImportedCell,
                sort: {
                    asc: AccrualImporterDraftSort.CREATED_AT_ASC,
                    desc: AccrualImporterDraftSort.CREATED_AT_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [AccrualImporterColumn.TOTAL]: {
                accessorKey: "id",
                name: "Amount",
                header: "Amount",
                cell: TotalCell,
                align: "right",
                sort: {
                    asc: AccrualImporterDraftSort.TOTAL_ASC,
                    desc: AccrualImporterDraftSort.TOTAL_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
            },
            [AccrualImporterColumn.ENTITY]: {
                accessorKey: "financialDocument",
                name: "Entity",
                header: "Entity",
                cell: EntityCell,
                sort: {
                    asc: AccrualImporterDraftSort.ENTITY_ASC,
                    desc: AccrualImporterDraftSort.ENTITY_DESC,
                    defaultOrder: "asc",
                },
                minSize: 160,
                maxSize: 220,
            },
            [AccrualImporterColumn.ACTIONS]: {
                accessorKey: "id",
                name: "Actions",
                header: "",
                cell: AccrualImporterActionsCell,
                minSize: 130,
                maxSize: 130,
                immutable: true,
                hiddenFromSettings: true,
            },
        },
    };
}

export function getDefaultAccrualImporterView(): AccrualImporterView {
    return {
        type: ViewType.ACCRUAL_IMPORTER,
        filters: {},
        options: { selection: true, bulkActions: true },
        columns: [
            {
                id: AccrualImporterColumn.DATE_IMPORTED,
                options: { pin: "left" },
            },
            { id: AccrualImporterColumn.STATUS },
            { id: AccrualImporterColumn.DOCUMENT_DATE },
            { id: AccrualImporterColumn.DUE_DATE },
            { id: AccrualImporterColumn.NUMBER },
            { id: AccrualImporterColumn.COUNTERPARTY },
            { id: AccrualImporterColumn.TOTAL },
            {
                id: AccrualImporterColumn.ENTITY,
                options: { pin: "right" },
            },
            {
                id: AccrualImporterColumn.ACTIONS,
                options: { pin: "right" },
            },
        ],
    };
}

export function getDefaultAccrualImporterLineItemView(
    mode: AccrualDocumentImporterMode,
): AccrualImporterView {
    const isInvoices = mode === "invoices";
    return {
        type: ViewType.ACCRUAL_IMPORTER,
        filters: {},
        options: { selection: true, bulkActions: true },
        groupBy: AccrualImporterGroupBy.LINE_ITEM,
        columns: [
            {
                id: AccrualImporterColumn.DATE_IMPORTED,
                options: { pin: "left" },
            },
            { id: AccrualImporterColumn.STATUS },
            { id: AccrualImporterColumn.COUNTERPARTY },
            ...(isInvoices ? [{ id: AccrualImporterColumn.ITEM }] : []),
            { id: AccrualImporterColumn.DESCRIPTION },
            { id: AccrualImporterColumn.ACCRUAL_ACCOUNT },
            { id: AccrualImporterColumn.BALANCE_ACCOUNT },
            { id: AccrualImporterColumn.DUE_DATE },
            { id: AccrualImporterColumn.TOTAL },
            {
                id: AccrualImporterColumn.ENTITY,
                options: { pin: "right" },
            },
            {
                id: AccrualImporterColumn.ACTIONS,
                options: { pin: "right" },
            },
        ],
    };
}

export function getBuiltInAccrualImporterViews(
    mode: AccrualDocumentImporterMode,
): Array<NamedView<AccrualImporterView>> {
    return [
        {
            ...getDefaultAccrualImporterView(),
            id: "default",
            name: mode === "invoices" ? "All invoices" : "All bills",
        },
    ];
}
